import { SyntheticEvent, useState } from 'react';
import { useRouter } from 'next/router';
import styled, { css } from 'styled-components';
import config from 'config/config';
import i18n from 'src/utils/translate';
import { capitalize } from 'lodash';
import MobileSearchInput from './mobileSearchInput';
import { titleSmall } from 'styles-js/mixins/typography';
import { CurrentUser } from 'src/types/user';
import { footfall, footfallExternal } from './sitebarFootfall';
import Icon, { Icon as IconType } from 'src/components/icon';

type Props = {
  currentUser: CurrentUser,
  unreadMessages: number,
  unreadNotifications: number,
};

const SiteBar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  height: 50px;
  z-index: 1049;
  background-color: ${props => props.theme.colorWhite};
  border-bottom: ${({theme}) => theme.greyLine};

  @media (${({theme}) => theme.overScreenSmall}) {
    display: none;
  }
`;

const Link = styled.a<{ isActive: boolean }>`
  display: flex;
  width: 20%;
  align-items: center;
  flex-direction: column;

  &:focus, &:hover, &:active {
    background-color: rgba(45, 164, 109, 0.05);

    span {
      color: ${({theme}) => theme.colorBlack};
    }
  }

  ${({isActive}) => isActive && css`
    background-color: rgba(45, 164, 109, 0.05);
  `};
`;

const LinkText = styled.span<{ isActive: boolean }>`
  ${titleSmall};
  font-size: 10px;
  line-height: 19px;
  font-weight: ${({theme}) => theme.fontWeightBoldTitle};
  color: ${({theme, isActive}) => isActive ? theme.colorBlack : theme.colorGreyDark};
`;

const ImageContainer = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  flex-grow: 2;
  align-items: center;
  justify-content: center;
  padding-top: 6px;
`;

const StyledIcon = styled(Icon)<{ isActive: boolean }>`
  font-size: 22px;
  color: ${({theme, isActive}) => isActive ? theme.colorBlack : theme.colorGreyDark};
`;

const Logo = styled.img`
  position: absolute;
  top: 2px;
  left: 0;
  right: 0;
  width: 34px;
  height: 34px;
  margin: auto;
`;

const Avatar = styled.img`
  width: 22px;
  height: 22px;
  border-radius: 50%;
`;

const Counter = styled.div`
  position: absolute;
  top: 12px;
  right: 40%;
  min-width: 20px;
  height: 20px;
  margin-right: -20px;
  padding: 0 5px;
  background-color: ${({theme}) => theme.colorBadgeNew};
  color: ${({theme}) => theme.colorBlack};
  border-radius: 10px;
  font-size: 12px;
  line-height: 20px;
  text-align: center;
`;

const SearchContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  z-index: 1006;
  background-color: ${props => props.theme.colorWhite};
`;

type ItemProps = {
  item: Item;
  unreadMessages: number;
  unreadNotifications: number;
}

const Item = ({item, unreadMessages, unreadNotifications}: ItemProps) => {
  const router = useRouter();
  // router.query.pathname = for my-hub and explore, router.route for search
  const currentRoute = router.query.pathname ? (router.query.pathname as string).split('/')[1] : router.route.split('/')[1];
  const isItemActive = item.links.includes(currentRoute);
  const counter = item.links.includes('notifications') ? unreadNotifications
    : item.links.includes('messages') ? unreadMessages : 0;

  return (
    <Link
      href={`/${item.links[0]}`}
      isActive={isItemActive}
      onClick={(e) => item.onClick(e)}
    >
      <ImageContainer>
        {item.icon && <StyledIcon icon={item.icon} isActive={isItemActive} />}
        {item.imageType === 'logo' && <Logo alt="HealthUnlocked small logo" src={item.imageUrl} />}
        {item.imageUrl && item.imageType === 'avatar' && <Avatar alt="Your avatar" src={item.imageUrl} />}
        {counter > 0 && <Counter>{counter}</Counter>}
      </ImageContainer>
      <LinkText isActive={isItemActive}>{capitalize(item.name)}</LinkText>
    </Link>
  );
};

type Item = {
  name: string;
  links: string[];
  imageUrl?: string;
  imageType?: 'logo' | 'avatar';
  icon?: IconType;
  onClick: (e: SyntheticEvent) => void;
}

const MobileSiteBar = ({ currentUser, unreadMessages, unreadNotifications }: Props) => {
  const [isSearching, setIsSearching] = useState(false);

  const items: Item[] = [
    {
      name: i18n.t('my feed'),
      links: ['', 'followed', 'explore'],
      imageUrl: `${config.cdn.static}img/logos/H-logo-speech-bubble-green.svg`,
      imageType: 'logo',
      onClick: (e: SyntheticEvent) => footfallExternal(e, '/', 'home'),
    },
    {
      name: i18n.t('my hub'),
      links: ['my-hub'],
      icon: 'communities',
      onClick: () => footfall('my-hub'),
    },
    {
      name: i18n.t('chat'),
      links: ['messages'],
      icon: 'paper-plane',
      onClick: (e: SyntheticEvent) => footfallExternal(e, '/messages', 'chat'),
    },
    {
      name: i18n.t('alerts'),
      links: ['notifications'],
      icon: 'bell-2',
      onClick: () => footfall('alerts'),
    },
    {
      name: i18n.t('search'),
      links: ['search'],
      icon: 'search-1',
      onClick: () => { setIsSearching(true); footfall('search'); },
    },
    {
      name: i18n.t('more'),
      links: ['more-list'],
      imageUrl: currentUser.avatarImageUrl,
      imageType: 'avatar',
      onClick: () => footfall('more'),
    },
  ];

  return (
    <SiteBar>
      {items.map(item =>
        <Item
          item={item}
          key={item.name}
          unreadMessages={unreadMessages}
          unreadNotifications={unreadNotifications}
        />,
      )}

      {isSearching && (
        <SearchContainer>
          <MobileSearchInput onCancel={() => setIsSearching(false)} />
        </SearchContainer>
      )}
    </SiteBar>
  );
};

export default MobileSiteBar;
